/**
 * 我的表情包/创建包页纯逻辑（14.17b）。
 *
 * 旧版对照（my_sticker_packs_page.dart / create_sticker_pack_page.dart）：
 * - 包展示名 = intro 非空取 intro，否则「表情包 <id>」（RemoteStickerPack.displayName）。
 * - 状态 chip：0 待审核（橙）/ 1 已通过（绿）/ 2 已拒绝（红）。
 * - 批量选图预检按「数量」聚合提示（N 张超过 100KB 已跳过；N 张格式不支持），
 *   超出 20 张上限的尾部静默丢弃（旧版 picked.take(remain) 同口径）。
 */
import {
  STICKER_PACK_MAX_ITEMS,
  STICKER_STATUS_APPROVED,
  STICKER_STATUS_REJECTED,
  stickerFileRejection,
} from '~/chat/stickerModel';

export function packDisplayName(pack: { readonly id: number; readonly intro: string }): string {
  const intro = pack.intro.trim();
  return intro !== '' ? intro : `表情包 ${pack.id}`;
}

export type PackStatusTone = 'pending' | 'approved' | 'rejected';

export function packStatusChip(status: number): { label: string; tone: PackStatusTone } {
  if (status === STICKER_STATUS_APPROVED) return { label: '已通过', tone: 'approved' };
  if (status === STICKER_STATUS_REJECTED) return { label: '已拒绝', tone: 'rejected' };
  return { label: '待审核', tone: 'pending' };
}

/** 行副标题：N 个表情（被拒且有原因时追加 · 原因，旧版同口径） */
export function packSubtitle(pack: {
  readonly status: number;
  readonly rejectReason: string;
  readonly items: readonly unknown[];
}): string {
  const base = `${pack.items.length} 个表情`;
  return pack.status === STICKER_STATUS_REJECTED && pack.rejectReason !== ''
    ? `${base} · ${pack.rejectReason}`
    : base;
}

/**
 * 批量选图预检（纯函数；File 只看 name/size，真实内容服务端按 MIME 兜底）。
 * currentCount=网格已有张数；超过 20 上限的尾部丢弃不提示（旧版同口径）。
 */
export function partitionStickerFiles<T extends { readonly name: string; readonly size: number }>(
  files: readonly T[],
  currentCount: number,
): { accepted: T[]; tip: string } {
  const remain = Math.max(0, STICKER_PACK_MAX_ITEMS - currentCount);
  const accepted: T[] = [];
  let oversize = 0;
  let badType = 0;
  for (const file of files.slice(0, remain)) {
    const reason = stickerFileRejection(file.name, file.size);
    if (reason === null) accepted.push(file);
    else if (reason.includes('100KB')) oversize += 1;
    else badType += 1;
  }
  const tips: string[] = [];
  if (oversize > 0) tips.push(`${oversize} 张超过 100KB 已跳过`);
  if (badType > 0) tips.push(`${badType} 张格式不支持（仅 png/jpg/gif/webp）`);
  return { accepted, tip: tips.join('；') };
}
